import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { useI18n } from "@/App";
import { WHATSAPP_ADMIN } from "@/lib/i18n";

const API = `/api`;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

function Field({ field, value, onChange }) {
  return (
    <div>
      <label className="bz-label" htmlFor={`field-${field.name}`}>
        {field.label}{field.required && <span className="text-emerald-700 ml-1">*</span>}
      </label>
      <input
        id={`field-${field.name}`}
        data-testid={`input-${field.name}`}
        className="bz-input"
        type={field.type || "text"}
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.hint || ""}
      />
    </div>
  );
}

export default function RoleModal({ role, open, onOpenChange }) {
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const [common, setCommon] = useState({ name: "", phone: "", location: "", email: "" });
  const [fields, setFields] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setCommon({ name: "", phone: "", location: "", email: "" });
      setFields({});
      setNotes("");
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open, role]);

  if (!role) return null;

  const handleCommonChange = (name, value) => setCommon((p) => ({ ...p, [name]: value }));
  const handleFieldChange = (name, value) => setFields((p) => ({ ...p, [name]: value }));

  const validate = () => {
    if (!common.name.trim() || !common.phone.trim() || !common.location.trim()) {
      toast.error("Please fill Name, Phone and Location");
      return false;
    }
    if (!/^\+?\d[\d\s-]{6,}$/.test(common.phone.trim())) {
      toast.error("Enter a valid phone number");
      return false;
    }
    for (const f of role.fields) {
      if (f.required && !String(fields[f.name] || "").trim()) {
        toast.error(`${f.label} is required`);
        return false;
      }
    }
    return true;
  };

  const buildWhatsAppMessage = () => {
    const lines = [
      `🌱 *Biomass Zone — New ${role.title} Lead*`,
      ``,
      `• *Name:* ${common.name}`,
      `• *Phone:* ${common.phone}`,
      `• *Location:* ${common.location}`,
    ];
    if (common.email) lines.push(`• *Email:* ${common.email}`);
    role.fields.forEach((f) => {
      if (fields[f.name]) lines.push(`• *${f.label}:* ${fields[f.name]}`);
    });
    if (notes) lines.push(`• *Notes:* ${notes}`);
    lines.push(``, `_Sent from biomasszone.com_`);
    return encodeURIComponent(lines.join("\n"));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        role: role.key,
        name: common.name.trim(),
        phone: common.phone.trim(),
        location: common.location.trim(),
        email: common.email.trim() || null,
        notes: notes.trim() || null,
        fields,
      });
      setSuccess(true);
      toast.success(t("form_success"));
      setTimeout(() => onOpenChange(false), 1800);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const FormBody = (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="role-form">
      {success ? (
        <div className="flex flex-col items-center py-8 text-center" data-testid="success-state">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <p className="text-lg font-display mt-3 text-slate-900">{t("form_success")}</p>
          <p className="text-sm text-slate-500 mt-2 inline-flex items-center gap-1">
            <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp opened for confirmation
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field field={{ name: "name", label: t("form_name"), required: true }} value={common.name} onChange={handleCommonChange} />
            <Field field={{ name: "phone", label: t("form_phone"), required: true, hint: "10-digit mobile" }} value={common.phone} onChange={handleCommonChange} />
            <Field field={{ name: "location", label: t("form_location"), required: true }} value={common.location} onChange={handleCommonChange} />
            <Field field={{ name: "email", label: t("form_email"), type: "email" }} value={common.email} onChange={handleCommonChange} />
          </div>
          <div className="pt-2">
            <div className="bz-label">{t("role_specific")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {role.fields.map((f) => (
                <Field key={f.name} field={f} value={fields[f.name]} onChange={handleFieldChange} />
              ))}
            </div>
          </div>
          <div>
            <label className="bz-label" htmlFor="notes">{t("form_notes")}</label>
            <textarea id="notes" data-testid="input-notes" className="bz-input min-h-[80px]"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else?"/>
          </div>
          <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
            <p className="text-xs text-slate-500 max-w-xs">
              On submit we'll open WhatsApp to auto-share this with our team.
            </p>
            <Button type="submit" data-testid="submit-role-form" disabled={submitting}
              className="bz-btn-primary rounded-full px-6 py-3 h-auto text-white">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("form_sending")}</>) : (<>{t("form_submit")}</>)}
            </Button>
          </div>
        </>
      )}
    </form>
  );

  const Title = (
    <>
      <span className="text-emerald-700 text-xs tracking-widest uppercase font-semibold">{t("form_title")}</span>
      <div className="font-display text-2xl mt-1 text-slate-900">{role.title}</div>
    </>
  );
  const Desc = <span className="text-slate-500 text-sm">{role.description}</span>;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="role-modal-drawer" className="bg-white text-slate-900 max-h-[92vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{Title}</DrawerTitle>
            <DrawerDescription>{Desc}</DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto">{FormBody}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="role-modal-dialog"
        className="bg-white text-slate-900 sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{Title}</DialogTitle>
          <DialogDescription>{Desc}</DialogDescription>
        </DialogHeader>
        {FormBody}
      </DialogContent>
    </Dialog>
  );
}
